import { Fragment, useMemo, useState } from 'react'
import get from 'lodash/get'
import pick from 'lodash/pick'
import moment from 'moment'
import Head from 'next/head'
import PropTypes from 'prop-types'

import QuoteCardWrapper from './QuoteCardWrapper'
import { interpolate, useTranslations } from 'common/language'
import { Container, Typography } from 'common/widgets'
import classNames from './styles.module.scss'


QuoteListView.propTypes = {
  quote: PropTypes.shape({
    options: PropTypes.array,
    dpa_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    has_booker: PropTypes.bool,
    group_operation: PropTypes.string,
    expired: PropTypes.string,
    is_early_bird: PropTypes.bool,
  }).isRequired,
  uuid: PropTypes.string.isRequired,
}

export default function QuoteListView({ quote, uuid }) {
  const { gettext, languageCode } = useTranslations()
  const [isOpen, setOpen] = useState(null)
  const isGroupOperation = useMemo(() => Boolean(quote.group_operation), [quote.group_operation])
  const quoteCard = useMemo(_ => (
    quote.options ? quote.options.map((item, ind) => {
      const index = ind + 1
      const data = {
        ...item,
        school: pick(get(item, 'school', {}), ['id', 'name', 'slug', 'static_content']),
      }
      return (
        <QuoteCardWrapper
          key={data.id}
          option={data.id}
          quote={uuid}
          ind={index}
          data={data}
          setOpen={setOpen}
          isOpen={isOpen === index}
          firstName={quote.first_name}
          lastName={quote.last_name}
          hasBooker={quote.has_booker}
          isGroupOperation={isGroupOperation}
        />
      )
    }) : null
  ), [quote.options, isOpen, setOpen])
  const origin = useMemo(() => typeof window === 'undefined' ? process.env.ORIGIN_URL : window.location.origin, [])
  const title = useMemo(() => isGroupOperation ? interpolate(gettext('Quote for %s'), [quote.dpa_name]) : interpolate(gettext('Quote for %s %s'), [quote.first_name, quote.last_name]), [quote.first_name, quote.last_name, quote.dpa_name, isGroupOperation])
  const description = useMemo(() => interpolate(gettext('Valid until %s'), [moment(quote.expired).format('DD.MM.YYYY')]), [quote.expired])
  const earlyBird = useMemo(() => quote.is_early_bird ? <span className={classNames.earlyBird}>{gettext('Early bird offer')}</span> : null, [quote.is_early_bird])
  return (
    <Fragment>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} key="description" />
        <meta property="og:title" content={title} key="og-title" />
        <meta property="og:description" content={description} key="og-description" />
        <meta property="og:url" content={origin + '/' + languageCode + '/quote/list/' + uuid} key="og-url" />
        <meta property="og:type" content="website" key="og-type" />
        <meta name="robots" content="noindex" />
      </Head>
      <div className={classNames.quoteLayout}>
        <div className={classNames.nameBlock}>
          <Container size="xl">
            <Typography variant="h2" className={classNames.nameText}>
              {title}
            </Typography>
            <Typography variant="subhead">
              {earlyBird} {description}
            </Typography>
          </Container>
        </div>
        <Container size="xl" className={classNames.quoteContainer}>
          <div className={classNames.quoteRow}>
            {quoteCard}
          </div>
        </Container>
        <Container size="xl" className={classNames.quotePrintContainer}>
          <div className={classNames.quoteRow}>
            {quoteCard.slice(0, 2)}
          </div>
          {quoteCard[2] && (
            <div className={classNames.quoteLastRow}>
              {quoteCard[2]}
            </div>
          )}
        </Container>
      </div>
    </Fragment>
  )
}
